// Read-only Sites (Address Book) view for the partner-viewer portal.
//
// A partner viewer can see — but not manage — their partner's recurring
// locations. Data comes from the JWT-scoped `GET /partners/me/sites`
// endpoint (the admin `/partners/:id/sites` route is ADMIN-only and would
// 403 + leak a partnerId). No toolbar, no row actions, no modals — this
// is deliberately a plain read-only view, distinct from the admin
// PartnerSites component.
//
// OVO-169 — a Catalog / Map toggle: the Map reuses the shared SitesMapView,
// passing the partner-scoped passages endpoint (404 on another partner's site).

import React, { useEffect, useState } from 'react';
import { Card, Segmented, Space, Table, Tag, message } from 'antd';
import { EnvironmentOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { getMySites, getMySitePassages } from '../modules/sites/api';
import SitesMapView from '../components/SitesMapView';

export default function PartnerSitesReadOnly() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('catalog');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { response, error } = await getMySites();
        if (error) throw new Error(error.message || 'Failed to load sites');
        if (!cancelled) setSites(Array.isArray(response) ? response : []);
      } catch (e) {
        if (!cancelled) message.error(e?.message || 'Failed to load sites');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, r) => (
        <span>
          {name}
          {r.isArchived && (
            <Tag style={{ marginLeft: 8 }} color="default">
              archived
            </Tag>
          )}
        </span>
      ),
    },
    { title: 'Address', dataIndex: 'address', key: 'address' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (v) => v || '—' },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', render: (v) => v || '—' },
  ];

  return (
    <Card
      size="small"
      bordered={false}
      bodyStyle={{ padding: view === 'map' ? 0 : undefined }}
      title={
        <Space>
          <Segmented
            size="small"
            value={view}
            onChange={setView}
            options={[
              { label: 'Catalog', value: 'catalog', icon: <UnorderedListOutlined /> },
              { label: 'Map', value: 'map', icon: <EnvironmentOutlined /> },
            ]}
          />
        </Space>
      }
    >
      {view === 'map' ? (
        <SitesMapView sites={sites} getPassages={getMySitePassages} />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={sites}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          locale={{ emptyText: 'No sites for your account yet' }}
        />
      )}
    </Card>
  );
}
